import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { LogOut, Shield, User, Mail, UserCheck, LayoutDashboard, BarChart3, Globe, Building2, Users, CreditCard, Briefcase, ShieldAlert, HeartPulse } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Super Admin sidebar. Every platform-health / QA / readiness /
 * workflow-diagnostic surface is consolidated behind a single "Platform
 * Monitoring" hub at /superadmin/platform so this nav stays focused on
 * platform-management workflows (agencies, staff, billing, moderation).
 */
const superAdminNav = [
  { title: 'Command Center', url: '/superadmin', icon: LayoutDashboard, end: true, key: 'overview' },
  { title: 'Platform Integrity', url: '/superadmin/platform-integrity', icon: ShieldAlert, end: true, key: 'integrity' },
  { title: 'Platform Monitoring', url: '/superadmin/platform', icon: HeartPulse, end: false, key: 'platform' },
  { title: 'Agencies', url: '/superadmin/agencies', icon: Building2, end: true, key: 'agencies' },
  { title: 'AM Portfolio', url: '/superadmin/portfolio', icon: Briefcase, end: true, key: 'portfolio' },
  { title: 'Audit Feed', url: '/superadmin/audit', icon: UserCheck, end: true, key: 'audit' },
  { title: 'Platform Staff', url: '/superadmin/staff', icon: Users, end: true, key: 'staff' },
  { title: 'Billing', url: '/superadmin/billing', icon: CreditCard, end: true, key: 'billing' },
  { title: 'Revenue Command', url: '/superadmin/revenue', icon: CreditCard, end: true, key: 'revenue' },
  { title: 'Platform Metrics', url: '/superadmin/metrics', icon: BarChart3, end: true, key: 'metrics' },
  { title: 'Platform Stats', url: '/superadmin/stats', icon: Globe, end: true, key: 'stats' },
  { title: 'Moderation', url: '/superadmin/moderation', icon: ShieldAlert, end: true, key: 'moderation' },
  { title: 'Email Settings', url: '/superadmin/emails', icon: Mail, end: true, key: 'emails' },
];


export default function SuperAdminLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['admin-applications-pending-count'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_pending_applications');
      if (error) return 0;
      return (data ?? []).filter((r: any) => r.approval_status === 'pending').length;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <div className="min-h-screen flex w-full bg-[#FAFAFA]">
      {/* Sidebar */}
      <div className="w-[220px] border-r border-[#E5E5E5] bg-white flex flex-col shrink-0">
        <div className="px-4 py-5">
          <div className="flex flex-col">
            <span className="text-[#111111] font-semibold text-lg tracking-tight"><span style={{ fontFamily: '-apple-system, sans-serif' }}>FlowTok <span className="text-[#1E8FE1]">OS</span></span></span>
            <span className="text-[11px] text-[#A3A3A3]">Platform Admin</span>
          </div>
        </div>
        <nav className="flex-1 px-3 pt-2 space-y-0.5">
          {superAdminNav.map(item => (
            <NavLink
              key={item.title}
              to={item.url}
              end={item.end}
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-[#525252] hover:text-[#111111] hover:bg-[#F5F5F5] transition-colors"
              activeClassName="bg-[#E8F4FD] text-[#1E8FE1] font-medium"
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{item.title}</span>
              {item.key === 'approvals' && pendingCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F5A623] text-white text-[10px] font-semibold">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#E5E5E5] p-3">
          <div className="flex items-center gap-2.5 rounded-md px-2 py-2 bg-[#FAFAFA]">
            <div className="h-8 w-8 rounded-full bg-[#1E8FE1] flex items-center justify-center shrink-0">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[12px] font-semibold text-[#111111] truncate">FlowTok OS Platform Admin</span>
              <span className="text-[10px] text-[#A3A3A3] truncate">Platform Admin Command Center</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 flex items-center justify-end px-6 glass-header sticky top-0 z-10">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="ftos-header-icon gap-2 rounded-md">
                <div className="h-7 w-7 rounded-full flex items-center justify-center ring-1 ring-white/15" style={{ background: 'linear-gradient(135deg, #A855F7 0%, #22D3EE 100%)' }}>
                  <User className="h-3.5 w-3.5 text-white" />
                </div>
                <span className="hidden sm:inline text-sm font-medium text-white/85">{user?.email}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-48 p-2 surface-raised">
              <Button variant="ghost" size="sm" className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => { setOpen(false); signOut(); }}>
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </Button>
            </PopoverContent>
          </Popover>
        </header>
        <main className="flex-1 p-8 overflow-auto page-fade-in max-w-[1280px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
